import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import Collapse from '../Collapse/Collapse';
import FAQNav from './FAQNav';
import { getFaqCategories, getFaqs } from '@/services/strapiService';
import type { FAQ } from '@/types/faq';
import Loader from '../Loader/Loader';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import type { FAQCategory } from '@/types/faq';
import { T } from '@tolgee/react';

const FAQSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [faqItems, setFaqItems] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  const locale = useSelectedLanguage();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategories([]);
        setFaqs([]);
        setFaqItems([]);

        const response = await getFaqCategories(locale);
        const fetchedCategories = response.data.map(
          (category: FAQCategory) => category.attributes.name,
        );
        setCategories(fetchedCategories);
        if (fetchedCategories.length > 0) {
          setActiveCategory(fetchedCategories[0]);
        }
      } catch (error) {
        console.error('Error fetching FAQ categories:', error);
      }
    };

    void fetchCategories();
  }, [locale]);

  useEffect(() => {
    if (!activeCategory) return;
    const fetchFAQs = async () => {
      setLoading(true);
      try {
        const response = await getFaqs(locale);
        const allFaqs = response.data;
        setFaqs(allFaqs);
        const filteredFaqs = allFaqs.filter(
          (faq) => faq.attributes?.faq_category?.data?.attributes.name === activeCategory,
        );
        setFaqItems(filteredFaqs);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        setLoading(false);
      }
    };
    void fetchFAQs();
  }, [activeCategory, locale]);

  useEffect(() => {
    if (!activeCategory) return;

    const filterFAQs = async () => {
      setLoading(true);
      try {
        const filteredFaqs = faqs.filter(
          (faq) => faq.attributes?.faq_category?.data?.attributes.name === activeCategory,
        );
        setFaqItems(filteredFaqs);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        setLoading(false);
      }
    };
    void filterFAQs();
  }, [activeCategory]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
  };

  return (
    <section className="p-5 md:p-12 relative">
      <div className="block md:flex justify-between items-start">
        <div className="max-w-full  mb-8 md:mb-0">
          <h2 className="text-4xl md:text-6xl pb-4">
            <T>faq.title.label</T>
          </h2>
          {categories.length > 0 ? (
            <FAQNav
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
            />
          ) : (
            <Loader position={'absolute'} />
          )}
        </div>
        <div className="w-full md:w-[881px]">
          <div className="mb-8 hidden md:flex justify-end">
            <div className="w-48 inline-block">
              {/* <Button variant="primary" href="/acute-help">
                Anfrage stellen
              </Button> */}
            </div>
          </div>
          {loading ? (
            <Loader position={'absolute'} />
          ) : (
            <div className="flex flex-col items-start gap-[15px] flex-shrink-0 w-full">
              {faqItems.length > 0 ? (
                faqItems.map((item) => (
                  <Collapse
                    key={item.id}
                    header={
                      <h3 className="text-sm md:text-lg font-medium">{item.attributes.question}</h3>
                    }
                  >
                    <p className="font-poppins">{item.attributes.answer}</p>
                  </Collapse>
                ))
              ) : (
                <p>No FAQs available for this category.</p>
              )}
            </div>
          )}
          <div className="text-right mt-8 flex md:hidden">
            <div className="w-48 inline-block">
              <Button variant="primary">Submit request</Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
